import { Injectable } from '@angular/core';
import { FmQuotationLine, FmTask } from '@core/api';
import { FmQuotation } from '@core/api/smart-portal.api';
import { QuotationTypes } from '@shared/constants/quotation-types.constants';

/**
 * Kapselt das Auslesen und Klassifizieren von Quotations (Angeboten)
 * eines Tasks – insbesondere die Sonderfälle für Nachtragsangebote
 * (Addendum) und deren akzeptierte Varianten.
 */
@Injectable()
export class TaskQuotationService {
  readonly mainOfferTypeId = 'affinis_offertype-100000000';
  readonly addendumOfferTypeId = 'affinis_offertype-100000001';

  getIncidentQuotations(task?: FmTask): FmQuotation[] {
    return [...((task?.incident?.fmQuotation ?? []) as FmQuotation[])];
  }

  isAddendumQuotation(quotation: FmQuotation | any): boolean {
    return (
      quotation?.type?.id === this.addendumOfferTypeId ||
      (quotation as any)?.typeId === this.addendumOfferTypeId ||
      quotation?.type?.defaultName === QuotationTypes.AddendumOffer
    );
  }

  isAcceptedAddendumStatusReason(statusReason?: string | null): boolean {
    return /^(angenommen|accepted)$/i.test(String(statusReason ?? '').trim());
  }

  getLatestAddendumQuotation(task?: FmTask): FmQuotation | undefined {
    const addendums = this.getIncidentQuotations(task).filter(q =>
      this.isAddendumQuotation(q)
    );

    return addendums.sort(
      (a, b) =>
        new Date(b?.changeDate || 0).getTime() -
        new Date(a?.changeDate || 0).getTime()
    )[0];
  }

  getLatestAcceptedAddendumQuotation(task?: FmTask): FmQuotation | undefined {
    return this.getIncidentQuotations(task)
      .filter(
        quotation =>
          this.isAddendumQuotation(quotation) &&
          this.isAcceptedAddendumStatusReason(
            quotation?.statusReason?.defaultName
          )
      )
      .sort(
        (a, b) =>
          new Date(b?.changeDate || 0).getTime() -
          new Date(a?.changeDate || 0).getTime()
      )[0];
  }

  getLatestAddendumStatusReason(task?: FmTask): string {
    const latest = this.getLatestAddendumQuotation(task);
    return latest?.statusReason?.defaultName || '-';
  }

  getLatestAddendumQuotationLines(task?: FmTask): FmQuotationLine[] {
    const latest = this.getLatestAddendumQuotation(task);
    const statusReason = latest?.statusReason?.defaultName || '';
    const lines = latest?.fmQuotationLine || [];

    return [...lines]
      .sort((a, b) => (a.recordNumber || 0) - (b.recordNumber || 0))
      .map(line => ({
        ...line,
        serviceProviderCommentAddendum:
          (line as any)?.serviceProviderCommentAddendum ||
          (line as any)?.serviceProviderComment ||
          '',
        addendumStatusReason:
          (line as any)?.addendumStatusReason || statusReason,
      }));
  }

  /** Hat der Vorgang bereits irgendein Nachtragsangebot (gespeichert oder gerade lokal eingereicht)? */
  hasExistingAddendumOffer(
    task: FmTask | undefined,
    submittedLocally: boolean
  ): boolean {
    if (submittedLocally) {
      return true;
    }
    return this.getIncidentQuotations(task).some(q =>
      this.isAddendumQuotation(q)
    );
  }

  getQuotationForPurchaseOrder(
    purchaseOrder: any,
    task?: FmTask
  ): FmQuotation | undefined {
    if (!purchaseOrder) return undefined;

    if (purchaseOrder?.quotation) {
      return purchaseOrder.quotation as FmQuotation;
    }

    const quotationId = purchaseOrder?.quotationId;
    if (quotationId) {
      return this.getIncidentQuotations(task).find(
        quotation =>
          quotation?.id === quotationId ||
          (quotation as any)?.quotationId === quotationId
      );
    }

    const rawQuotation = (purchaseOrder as any)?.fmQuotation;
    if (Array.isArray(rawQuotation)) {
      return rawQuotation[0];
    }
    return rawQuotation;
  }
}
